/**
 * "N already in search — Remove them" banner (epic 2026-09-22, sub-task 05).
 *
 * Flow A's second half: a combined list arrives with the Thrive results it
 * INHERITED from its sources (no Compare runs on arrival since epic
 * 2026-10-01-combine-stability-perf), and this says how many of its rows are
 * already on the target search — the live count the server reads
 * (`CompareResult.inherited.in_search_remaining`), or a Compare the page
 * watched succeed. Mounted by `ImportDetailPage` only for a combined import
 * whose count is positive — a manual
 * Compare on any other import type never reaches this component (the page
 * gates on `import_format`, not this file, so a non-combined list's behaviour
 * is unchanged).
 *
 * "Already in search" is `already_in_search` PLUS `linkedin_position_update`
 * (already in the search, title changed since) — the SAME set the Combine
 * preview's "This search" removal uses (product decision 2026-09-23), so the
 * banner and the preview agree about who is already in the search.
 *
 * "Remove them" is `POST /imports/{id}/contacts/leave-out/bulk` with
 * `{group: "in_search"}` (the server expands it to both buckets) — the SAME three query keys
 * `CompareModalHost`'s own terminal-transition effect invalidates
 * (`["imports", id, "contacts"]`, `["import-contacts", id]`,
 * `["import-detail", id]`), so the contacts table and the Search card's
 * counts pick up the removal without a reload.
 *
 * `left_out_count` CAN come back `0` even though `count` (the dry-run's own
 * summary buckets) was positive: the bulk write selects on the
 * PER-CONTACT `import_contacts.compare_bucket` column, which only a
 * `compare_worker` run that has actually persisted it writes — a stale or
 * pre-migration row can be counted in the dry-run's summary bucket and still
 * have nothing there to select. Rather than toast a confusing "Removed 0
 * already in search", that case switches the banner's own copy to "Nothing to
 * remove" (Failure Cases, this sub-task's PRD).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@nu-assist/ui";

import type { CompareBuckets } from "@nu-assist/contracts";

import type { ImportsClient } from "../../../clients/imports.client";

/** How many people a Compare run found already in the list's search:
 *  `already_in_search` + `linkedin_position_update` — the in-search set the
 *  server's `group: "in_search"` covers. */
export function inSearchCount(
  buckets: Pick<CompareBuckets, "already_in_search" | "linkedin_position_update">
): number {
  return buckets.already_in_search + buckets.linkedin_position_update;
}

export interface AlreadyInSearchBannerProps {
  client: ImportsClient;
  importId: string;
  /** `inSearchCount(buckets)` from the run that just succeeded. The page
   *  only mounts this component once this is positive, but the guard is
   *  repeated here so the component is honest on its own. */
  count: number;
  /** Fired once the bucket has been genuinely cleared (`left_out_count > 0`).
   *  The page owns whether the banner is mounted at all; this only reports
   *  that there is nothing left for it to say. Never fired for the "Nothing
   *  to remove" outcome — that copy IS the terminal state, not a hand-off. */
  onRemoved?: () => void;
}

export function AlreadyInSearchBanner({
  client,
  importId,
  count,
  onRemoved,
}: AlreadyInSearchBannerProps) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => client.bulkLeaveOut(importId, { group: "in_search" }),
    onSuccess: (result) => {
      // Same three keys `CompareModalHost`'s terminal-transition effect
      // invalidates (coupling noted there too) — the contacts table, the
      // export panel and the Search card's counts all read one of these.
      queryClient.invalidateQueries({ queryKey: ["imports", importId, "contacts"] });
      queryClient.invalidateQueries({ queryKey: ["import-contacts", importId] });
      queryClient.invalidateQueries({ queryKey: ["import-detail", importId] });
      if (result.left_out_count > 0) {
        toast.success(`Removed ${result.left_out_count.toLocaleString()} already in search.`);
        onRemoved?.();
      }
      // `left_out_count === 0` renders "Nothing to remove" below instead —
      // see the module docstring for why this is not an error.
    },
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : "Couldn't remove those contacts. Try again.");
    },
  });

  if (count <= 0) return null;

  const nothingToRemove = mutation.isSuccess && mutation.data.left_out_count === 0;

  return (
    <div
      data-testid="already-in-search-banner"
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-bg-subtle px-4 py-3"
    >
      {nothingToRemove ? (
        <span data-testid="already-in-search-nothing" className="text-body text-text-secondary">
          Nothing to remove.
        </span>
      ) : (
        <>
          <span data-testid="already-in-search-count" className="text-body text-text">
            {count.toLocaleString()} already in search
          </span>
          <Button
            variant="secondary"
            type="button"
            data-testid="already-in-search-remove"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Remove them
          </Button>
        </>
      )}
    </div>
  );
}
